import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { RateListTable } from '~/modules/rateCard/advancedRateCard/components/rateListTable';
import { CostOverrideRateEditor } from './cellEditors/CostOverrideRateEditor';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    '& tbody tr td:last-child': {
      borderBottom: 'none'
    }
  },
  bottomDivider: {
    position: 'sticky',
    bottom: 0,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  emptyText: {
    padding: theme.spacing(2, 1),
    fontStyle: 'italic',
    color: theme.palette.text.secondary
  },
  costOverrideHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%'
  },
  costOverrideHeaderText: {
    flex: 1,
    textAlign: 'right'
  },
  costOverrideHeaderSpacer: {
    flex: '0 0 auto',
    width: theme.spacing(11)
  },
  cappedDimension: {
    maxWidth: theme.spacing(28)
  },
  effectiveDateColumn: {
    minWidth: theme.spacing(20)
  }
}));

const noop = () => {};
const EMPTY_ERRORS = {};
const COLUMN_RENDER_OVERRIDES = { amount: CostOverrideRateEditor };

export const CostOverridesTable = ({
  selectedDimensions,
  projectRates,
  formOnChange,
  projectSlug,
  projectCurrency,
  addNewEntry,
  setAddNewEntry,
  errors = EMPTY_ERRORS,
  hasMore = false,
  loadingMore = false,
  loadMore = noop,
  openDeleteDialog,
  readOnly = false
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const isEmpty = projectRates.length === 0;
  const isLocked = loadingMore;
  const bottomAnchorRef = useRef(null);
  const previousRateIdsRef = useRef(projectRates.map(rate => rate.id));

  useEffect(() => {
    const currentRateIds = projectRates.map(rate => rate.id);
    const previousRateIds = previousRateIdsRef.current;

    previousRateIdsRef.current = currentRateIds;

    const isSingleRowAppended =
      currentRateIds.length === previousRateIds.length + 1 &&
      previousRateIds.every((id, index) => id === currentRateIds[index]);

    if (isSingleRowAppended && !loadingMore && !hasMore) {
      bottomAnchorRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [projectRates, loadingMore, hasMore]);

  const rows = useMemo(
    () =>
      projectRates.map(rate => ({
        ...rate,
        totalEntries: 1,
        ...(isLocked ? { disabled: true } : {})
      })),
    [projectRates, isLocked]
  );
  const columnPropOverrides = useMemo(() => {
    const label = formatMessage({
      id: 'projectCostOverrides.columnHeader.costOverride'
    });
    const headerValue = readOnly ? (
      <div className={classes.costOverrideHeader}>
        <span className={classes.costOverrideHeaderText}>{label}</span>
        <span className={classes.costOverrideHeaderSpacer} aria-hidden="true" />
      </div>
    ) : (
      label
    );

    const overrides = {
      amount: {
        value: headerValue,
        align: 'left',
        onPercentChange: formOnChange.onPercentChange,
        onTypeChange: formOnChange.onTypeChange
      },
      effectiveDate: { className: classes.effectiveDateColumn }
    };

    if (!readOnly) {
      selectedDimensions.forEach(dimension => {
        overrides[dimension.id] = { className: classes.cappedDimension };
      });
    }

    return overrides;
  }, [
    classes.cappedDimension,
    classes.costOverrideHeader,
    classes.costOverrideHeaderSpacer,
    classes.costOverrideHeaderText,
    classes.effectiveDateColumn,
    formatMessage,
    formOnChange.onPercentChange,
    formOnChange.onTypeChange,
    readOnly,
    selectedDimensions
  ]);

  return (
    <div className={classes.root}>
      <RateListTable
        projectRates={rows}
        projectDimensions={selectedDimensions}
        formOnChange={formOnChange}
        hasMore={hasMore}
        loadingMore={loadingMore}
        loadMore={loadMore}
        errors={errors}
        openDeleteDialog={openDeleteDialog}
        projectSlug={projectSlug}
        addNewEntry={addNewEntry}
        setAddNewEntry={setAddNewEntry}
        projectCurrency={projectCurrency}
        readOnly={readOnly}
        firstRowDeletable
        columnRenderOverrides={COLUMN_RENDER_OVERRIDES}
        columnPropOverrides={columnPropOverrides}
      />
      {isEmpty ? (
        <Typography
          variant="body2"
          className={classes.emptyText}
          data-qe-id="CostOverridesTableInfoText"
        >
          <FormattedMessage id="projectCostOverrides.dialog.noCostOverridesSet" />
        </Typography>
      ) : null}
      <div ref={bottomAnchorRef} aria-hidden="true" />
      <div className={classes.bottomDivider} aria-hidden="true" />
    </div>
  );
};

CostOverridesTable.propTypes = {
  selectedDimensions: PropTypes.array.isRequired,
  projectRates: PropTypes.array.isRequired,
  formOnChange: PropTypes.object.isRequired,
  projectSlug: PropTypes.string,
  projectCurrency: PropTypes.object,
  addNewEntry: PropTypes.object,
  setAddNewEntry: PropTypes.func,
  errors: PropTypes.object,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  loadMore: PropTypes.func,
  openDeleteDialog: PropTypes.func,
  readOnly: PropTypes.bool
};

export default CostOverridesTable;
